/**
 * @fileoverview TemplateLoader for loading page template-specific modules.
 * 
 * This loader handles modules that are specific to particular page templates,
 * enabling template-specific functionality and optimizations based on the
 * current page's template configuration.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { getPageSettings } from "../../util/pageSettings";
import { RouterHelper } from "../router-helper";
import { ITemplateLoader, ModuleLoaderStatus } from "../interfaces/module-loader.interfaces";
import { initOnceWithDOMReady } from "../../util/dom-loader-check";

/**
 * TemplateLoader manages modules specific to page templates.
 * 
 * This loader is responsible for:
 * - Detecting the current page template from page settings
 * - Loading template-specific modules and functionality
 * - Providing template-aware module loading capabilities
 * - Supporting various page templates (campaign pages, landing pages, etc.)
 * 
 * The TemplateLoader follows the singleton pattern and implements the ITemplateLoader
 * interface for consistent behavior across the router system.
 * 
 * Supported Templates:
 * - Campaign Page Template: Loads marketing landing page functionality
 * - Additional templates can be easily added with conditional loading
 * 
 * Features:
 * - Singleton pattern for global access
 * - Dynamic template detection via getPageSettings
 * - Secure module loading via RouterHelper
 * - Case-insensitive template matching
 * - Comprehensive error handling and status tracking
 * 
 * @class TemplateLoader
 * @implements {ITemplateLoader}
 */
export class TemplateLoader implements ITemplateLoader {
    /** Singleton instance of the TemplateLoader */
    private static instance: TemplateLoader;

    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();

    /** Current page template identifier detected from page settings */
    private pageTemplate = '';

    /** Current status and state of the TemplateLoader */
    private readonly status: ModuleLoaderStatus = {
        isInitialized: false,
        loadedModules: [],
        failedModules: [],
        isLoading: false
    };

    /**
     * Gets the singleton instance of TemplateLoader.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton TemplateLoader instance
     */
    static getInstance(): TemplateLoader {
        this.instance ??= new TemplateLoader();
        return this.instance;
    }

    /**
     * Initializes the TemplateLoader by detecting the current page template.
     * 
     * Retrieves the page template from page settings and stores it for
     * subsequent template-specific module loading decisions.
     * 
     * @returns Promise that resolves when initialization is complete
     * @throws {Error} When template detection or initialization fails
     */
    async init(): Promise<void> {
        try {
            this.pageTemplate = getPageSettings('pagetemplate') ?? "";
            this.status.isInitialized = true;
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    /**
     * Returns the human-readable name of this loader.
     * Used for logging, debugging, and status reporting.
     * @returns The loader's display name
     */
    getName(): string {
        return 'TemplateLoader';
    }

    /**
     * Returns a copy of the current loader status.
     * Provides real-time information about the loader's state.
     * @returns Current loader status information
     */
    getStatus(): ModuleLoaderStatus {
        return { ...this.status };
    }

    /**
     * Gets the current page template identifier.
     * 
     * Returns the template name that was detected during initialization,
     * used to determine which template-specific modules to load.
     * 
     * @returns The current page template name
     */
    getPageTemplate(): string {
        return this.pageTemplate;
    }

    /**
     * Loads all template-specific modules (delegates to templateModules).
     * 
     * This is the main entry point for loading template modules,
     * implementing the IModuleLoader interface contract.
     * 
     * @returns Promise that resolves when all template modules are loaded
     */
    async loadModules(): Promise<void> {
        return this.templateModules();
    }

    /**
     * Loads modules specific to the detected page template.
     * 
     * Currently supports:
     * - Campaign Page Template: Loads marketing landing page functionality
     * 
     * Uses case-insensitive matching to handle template name variations.
     * Additional templates can be easily added with new conditional blocks.
     * 
     * @returns Promise that resolves when template modules are loaded
     * @throws {Error} When template module loading fails
     */
    async templateModules(): Promise<void> {
        this.status.isLoading = true;

        try {

            const isCampaignPage = this.pageTemplate?.toLowerCase().includes('campaign page template');
            const pageTypeElement = document.getElementById('pageType') as HTMLInputElement | null;
            const pageType = pageTypeElement?.value?.toLowerCase();

            if (isCampaignPage) {
                // Import marketingLandingPage module dynamically
                const { MarketingLandingPage } = await import(
        /* webpackChunkName: "marketing-landing-loader" */ '../../marketing/marketingLandingPage'
                );

                // Initializes the marketing landing page singleton safely and re-runs init()
                // on bfcache restore to sync URL-driven promotional content.
                initOnceWithDOMReady('MarketingLandingPage', () => {
                    const marketingLandingPage = MarketingLandingPage.getInstance();
                    marketingLandingPage.init();
                }, { resetOnPersistedNavigation: true });
                this.status.loadedModules.push('marketing-landing');

                // Import marketingPage module dynamically (if separate file)
                const { MarketingPage } = await import(
        /* webpackChunkName: "marketing-page-loader" */ '../../marketing/marketing-page'
                );

                // Initializes MarketingPage with timing-safe guards. Both this loader and
                // marketing-page.ts share initialization state, so resetOnPersistedNavigation
                // is deferred to avoid desynchronizing the guards. Revisit both files if bfcache refresh is needed.
                if (pageTypeElement && pageType === 'opus 2 campaign page template') {
                    // Prevent attaching multiple DOMContentLoaded listeners

                        initOnceWithDOMReady('MarketingPage', () => {
                            // Prevent multiple MarketingPage initializations
                            if ((globalThis as any).__MarketingPageInitialized) return;
                            (globalThis as any).__MarketingPageInitialized = true;
                            const marketingPage = new MarketingPage();
                            marketingPage.localizationPhoneNumber();
                        });
                    
                    this.status.loadedModules.push('marketing-page');
                }
            }

            // NOTE: __OurTeamInitialized is also set independently by our-team-layout.ts's own
            // module-level DOMContentLoaded listener (same cross-file coupling as MarketingPage
            // above), so resetOnPersistedNavigation is intentionally not enabled here either.
            if (pageType === 'opus 2 our team page template') {
                const { OurTeam } = await import(
        /* webpackChunkName: "our-team-loader" */ '../../our_team/index'
                );


                    initOnceWithDOMReady('OurTeam', () => {
                        // Ensure the module initializes only once
                        if ((globalThis as any).__OurTeamInitialized) return;
                        (globalThis as any).__OurTeamInitialized = true;
                        OurTeam.getInstance();
                    });
                
                this.status.loadedModules.push('our-team');
            }
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        } finally {
            this.status.isLoading = false;
        }
    }
}
